// DashboardPanel — at-a-glance metrics for the CRO Audit tool.
//
// Shows: score distribution bar, average score, top 5 best/worst/failed,
// and per-category average bar chart.

import React, { useEffect, useState } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  ColumnLayout,
} from '@cloudscape-design/components';

import { storeCROAuditApi } from './storeCROAuditApi.js';

export default function DashboardPanel({ refreshTrigger }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    storeCROAuditApi
      .getDashboard()
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshTrigger]);

  if (loading || !data) return null;

  const { distribution, average_score, top5_best, top5_worst, top5_failed, category_averages, total_stores } = data;
  const total = distribution.green + distribution.yellow + distribution.red + distribution.failed;

  return (
    <Container header={<Header variant="h2">📊 Dashboard</Header>}>
      <SpaceBetween size="l">
        {/* Score distribution bar */}
        <Box>
          <Box variant="strong" margin={{ bottom: 'xxs' }}>
            Score distribution ({total_stores} stores)
          </Box>
          <div style={{ display: 'flex', height: 28, borderRadius: 6, overflow: 'hidden', background: '#f1f5f9' }}>
            {distribution.green > 0 && (
              <div style={{ flex: distribution.green, background: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 600 }}>
                {distribution.green}
              </div>
            )}
            {distribution.yellow > 0 && (
              <div style={{ flex: distribution.yellow, background: '#ca8a04', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 600 }}>
                {distribution.yellow}
              </div>
            )}
            {distribution.red > 0 && (
              <div style={{ flex: distribution.red, background: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 600 }}>
                {distribution.red}
              </div>
            )}
            {distribution.failed > 0 && (
              <div style={{ flex: distribution.failed, background: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 12, fontWeight: 600 }}>
                {distribution.failed}
              </div>
            )}
          </div>
          <Box margin={{ top: 'xxs' }} fontSize="body-s" color="text-body-secondary">
            <span style={{ color: '#16a34a' }}>● {distribution.green} green (≥80%)</span>
            {' · '}
            <span style={{ color: '#ca8a04' }}>● {distribution.yellow} yellow (50-80%)</span>
            {' · '}
            <span style={{ color: '#dc2626' }}>● {distribution.red} red (&lt;50%)</span>
            {distribution.failed > 0 && <>{' · '}<span style={{ color: '#64748b' }}>● {distribution.failed} failed</span></>}
          </Box>
        </Box>

        {/* Average score + Top 5s */}
        <ColumnLayout columns={3}>
          {/* Average */}
          <Box>
            <Box variant="strong" margin={{ bottom: 'xxs' }}>Average score</Box>
            <Box fontSize="display-l" fontWeight="bold" color={average_score >= 80 ? 'text-status-success' : average_score >= 50 ? 'text-status-warning' : 'text-status-error'}>
              {average_score}%
            </Box>
          </Box>

          {/* Top 5 Best */}
          <Box>
            <Box variant="strong" margin={{ bottom: 'xxs' }}>🏆 Top 5 stores</Box>
            {top5_best.map((s, i) => (
              <div key={s.shop_id} style={{ fontSize: 13, padding: '2px 0', display: 'flex', justifyContent: 'space-between' }}>
                <span>{i + 1}. {s.store_name || s.store_url}</span>
                <span style={{ color: '#16a34a', fontWeight: 600 }}>{s.score_percentage.toFixed(0)}%</span>
              </div>
            ))}
          </Box>

          {/* Top 5 Worst */}
          <Box>
            <Box variant="strong" margin={{ bottom: 'xxs' }}>⚠️ Bottom 5 stores</Box>
            {top5_worst.map((s, i) => (
              <div key={s.shop_id} style={{ fontSize: 13, padding: '2px 0', display: 'flex', justifyContent: 'space-between' }}>
                <span>{i + 1}. {s.store_name || s.store_url}</span>
                <span style={{ color: '#dc2626', fontWeight: 600 }}>{s.score_percentage.toFixed(0)}%</span>
              </div>
            ))}
          </Box>
        </ColumnLayout>

        {/* Per-category average bar chart */}
        <Box>
          <Box variant="strong" margin={{ bottom: 'xs' }}>Category performance (average across all stores)</Box>
          <SpaceBetween size="xs">
            {Object.entries(category_averages)
              .sort((a, b) => a[1].percentage - b[1].percentage)
              .map(([key, cat]) => (
                <div
                  key={key}
                  className="tu-pill-trigger"
                  style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  onMouseEnter={(e) => {
                    const tip = e.currentTarget.querySelector('.tu-pill-tip');
                    if (tip) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      tip.style.top = `${rect.bottom + 4}px`;
                      tip.style.left = `${rect.left + 140}px`;
                    }
                  }}
                >
                  <span style={{ width: 140, fontSize: 13, flexShrink: 0, cursor: 'default' }}>{cat.name}</span>
                  <div style={{ flex: 1, height: 20, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
                    <div style={{
                      width: `${cat.percentage}%`,
                      height: '100%',
                      background: cat.percentage >= 80 ? '#16a34a' : cat.percentage >= 50 ? '#ca8a04' : '#dc2626',
                      borderRadius: 4,
                      transition: 'width 0.5s ease',
                    }} />
                    <span style={{ position: 'absolute', right: 6, top: 2, fontSize: 11, fontWeight: 600, color: '#334155' }}>
                      {cat.percentage}%
                    </span>
                  </div>
                  <span className="tu-pill-tip" role="tooltip" style={{ whiteSpace: 'normal', maxWidth: 260, lineHeight: 1.4 }}>
                    <strong>{cat.name}</strong><br/>
                    {(cat.features || []).join(' · ')}
                  </span>
                </div>
              ))}
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    </Container>
  );
}
